import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import api from '../../lib/api';

export default function ActivationTab() {
    const [data, setData] = useState(null);
    const [pipeline, setPipeline] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/internal/v1/activation').catch(() => ({ data: null })),
            api.get('/internal/v1/pipeline-mrr').catch(() => ({ data: null }))
        ]).then(([a, p]) => { setData(a.data); setPipeline(p.data); }).finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="animate-pulse h-64 bg-slate-900/50 rounded-2xl" />;

    return (
        <div className="space-y-6">
            {data && (
                <Card className="bg-slate-900/50 border-white/10">
                    <CardHeader><CardTitle className="text-white">Taux d'activation</CardTitle></CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            {[
                                { label: 'A un mandat', value: `${data.rates.has_mandate}%`, color: 'text-green-400' },
                                { label: 'A un agent', value: `${data.rates.has_agent}%`, color: 'text-blue-400' },
                                { label: 'A un vendeur', value: `${data.rates.has_seller}%`, color: 'text-purple-400' },
                                { label: 'Feature avancée', value: `${data.rates.has_advanced_feature}%`, color: 'text-orange-400' }
                            ].map((r, i) => (
                                <div key={i} className="text-center p-4 rounded-xl bg-white/5">
                                    <p className={`text-2xl font-bold ${r.color}`}>{r.value}</p>
                                    <p className="text-xs text-white/50">{r.label}</p>
                                </div>
                            ))}
                        </div>
                        <h4 className="text-sm font-bold text-white mb-3">Détail par agence</h4>
                        <div className="space-y-2">
                            {data.agencies?.map((a, i) => (
                                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                                    <div className={`w-2 h-2 rounded-full ${a.status === 'active' ? 'bg-green-500' : a.status === 'partial' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                                    <span className="text-sm text-white font-medium flex-1">{a.name}</span>
                                    <div className="flex items-center gap-1">
                                        <div className="w-20 h-2 bg-white/10 rounded-full overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full" style={{ width: `${a.activation_depth}%` }} />
                                        </div>
                                        <span className="text-xs text-white/60 w-10 text-right">{a.activation_depth}%</span>
                                    </div>
                                    <span className="text-xs text-white/40">{a.mandates} mandats</span>
                                    <span className="text-xs text-white/40">{a.agents} agents</span>
                                    {a.time_to_first_mandate_hours && <span className="text-[10px] text-white/30">TTFM: {Math.round(a.time_to_first_mandate_hours)}h</span>}
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {pipeline && (
                <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-slate-900/50 border-white/10">
                        <CardHeader><CardTitle className="text-white">MRR Committed</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-4xl font-black text-green-400">{pipeline.committed.mrr}€<span className="text-lg text-white/40">/mois</span></p>
                            <p className="text-sm text-white/50 mt-1">ARR: {pipeline.committed.arr}€ | {pipeline.committed.agencies} agences</p>
                            <div className="mt-4 space-y-1">
                                {pipeline.committed.detail?.map((a, i) => (
                                    <div key={i} className="flex justify-between text-xs"><span className="text-white/60">{a.name} ({a.agents} agents)</span><span className="text-white font-bold">{a.mrr}€/mois</span></div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-slate-900/50 border-white/10">
                        <CardHeader><CardTitle className="text-white">Pipeline Weighted</CardTitle></CardHeader>
                        <CardContent>
                            <p className="text-4xl font-black text-purple-400">{pipeline.pipeline.weighted_pipeline_mrr}€<span className="text-lg text-white/40">/mois</span></p>
                            <p className="text-sm text-white/50 mt-1">Total pipeline: {pipeline.pipeline.total_pipeline_mrr}€</p>
                            <div className="mt-4 space-y-2">
                                {Object.entries(pipeline.pipeline.stages || {}).filter(([, v]) => v.count > 0).map(([k, v]) => (
                                    <div key={k} className="flex items-center gap-2 text-xs">
                                        <span className="text-white/60 w-24 capitalize">{k.replace('_', ' ')}</span>
                                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-purple-500 rounded-full" style={{ width: `${(v.weighted_mrr / Math.max(pipeline.pipeline.weighted_pipeline_mrr, 1)) * 100}%` }} />
                                        </div>
                                        <span className="text-white/40">{v.count} leads</span>
                                        <span className="text-white font-bold">{v.weighted_mrr}€</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
